import { ARCADE_FPS, progress1000 } from './common.js';
import type { ArcadeBaseState, ArcadeSimulation } from './common.js';
import { ArcadeRng } from './rng.js';

/**
 * Chope!: lunchtime at the hawker centre. Tables free up across a 3x3 food
 * court; tap one to chope it with a tissue packet before an auntie grabs it.
 * Tap an uncle's table and he scolds you (points lost, hands frozen for a
 * moment). Quick chopes and streaks score more.
 */

export const CH_COLS = 3;
export const CH_ROWS = 3;
export const CH_CELLS = CH_COLS * CH_ROWS;
export const CH_FRAMES = 60 * ARCADE_FPS;
/** Codes 1..9 tap cell code-1. */
export const CH_TAP_BASE = 1;

export type ChCellState = 'idle' | 'free' | 'uncle' | 'choped' | 'grabbed' | 'scolded';

export interface ChCell {
  state: ChCellState;
  /** Frame at which the current state ends (free/uncle/effects). */
  until: number;
  /** Frame the table became free, for the quick-chope bonus. */
  since: number;
}

export interface ChopeState extends ArcadeBaseState {
  rng: ArcadeRng;
  cells: ChCell[];
  nextSpawn: number;
  combo: number;
  multiplier: number;
  /** Taps are ignored until this frame (after an uncle scolds you). */
  stunnedUntil: number;
  choped: number;
  missed: number;
}

export const chope: ArcadeSimulation<ChopeState> = {
  maxFrames: CH_FRAMES,

  create(seed) {
    return {
      frame: 0,
      over: false,
      score: 0,
      fx: [],
      rng: new ArcadeRng(seed),
      cells: Array.from({ length: CH_CELLS }, () => ({ state: 'idle' as ChCellState, until: 0, since: 0 })),
      nextSpawn: 40,
      combo: 0,
      multiplier: 1,
      stunnedUntil: 0,
      choped: 0,
      missed: 0,
    };
  },

  validCode(code) {
    return code >= CH_TAP_BASE && code < CH_TAP_BASE + CH_CELLS;
  },

  step(s, codes) {
    if (s.over) return;
    s.fx = [];
    const f = s.frame;

    for (const code of codes) {
      if (f < s.stunnedUntil) continue;
      const index = code - CH_TAP_BASE;
      const cell = s.cells[index]!;
      if (cell.state === 'free') {
        const quick = Math.max(0, Math.floor((cell.until - f) / 6));
        const points = (10 + quick) * s.multiplier;
        s.score += points;
        s.combo += 1;
        s.multiplier = Math.min(4, 1 + Math.floor(s.combo / 4));
        s.choped += 1;
        cell.state = 'choped';
        cell.until = f + 24;
        s.fx.push({ kind: 'chope', x: index % CH_COLS, y: Math.floor(index / CH_COLS), value: points });
      } else if (cell.state === 'uncle') {
        s.score = Math.max(0, s.score - 15);
        s.combo = 0;
        s.multiplier = 1;
        s.stunnedUntil = f + 45;
        cell.state = 'scolded';
        cell.until = f + 30;
        s.fx.push({ kind: 'scold', x: index % CH_COLS, y: Math.floor(index / CH_COLS), value: -15 });
      }
    }

    // Timers: unchoped tables go to aunties; effects and uncles leave.
    for (let i = 0; i < CH_CELLS; i += 1) {
      const cell = s.cells[i]!;
      if (cell.state === 'idle' || f < cell.until) continue;
      if (cell.state === 'free') {
        cell.state = 'grabbed';
        cell.until = f + 28;
        s.combo = 0;
        s.multiplier = 1;
        s.missed += 1;
        s.fx.push({ kind: 'grabbed', x: i % CH_COLS, y: Math.floor(i / CH_COLS) });
      } else {
        cell.state = 'idle';
      }
    }

    if (f >= s.nextSpawn) {
      const p = progress1000(f, CH_FRAMES);
      const idle = s.cells.map((cell, i) => (cell.state === 'idle' ? i : -1)).filter((i) => i >= 0);
      if (idle.length > 0) {
        const index = idle[s.rng.int(0, idle.length)]!;
        const cell = s.cells[index]!;
        const uncle = f > 300 && s.rng.int(0, 100) < 22 + Math.floor((p * 10) / 1000);
        if (uncle) {
          cell.state = 'uncle';
          cell.until = f + s.rng.int(80, 130);
        } else {
          cell.state = 'free';
          cell.since = f;
          cell.until = f + 90 - Math.floor((p * 45) / 1000);
        }
      }
      s.nextSpawn = f + 48 - Math.floor((p * 28) / 1000) + s.rng.int(0, 8);
    }

    s.frame += 1;
    if (s.frame >= CH_FRAMES) s.over = true;
  },
};
